const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const HEADING_KEY_RE = /^h1-[0-9a-f]{16}-[1-9][0-9]{0,5}$/;
const MAX_FRAGMENT_LENGTH = 2048;

export type ConversationTarget =
  | { kind: "message"; messageId: string; headingKey?: string }
  | { kind: "thread"; threadId: string; messageId?: string }
  | { kind: "turn"; turnId: string };

export type ConversationTargetParseResult =
  | { state: "none" }
  | { state: "invalid"; reason: "ambiguous" | "duplicate" | "identifier" | "oversized" | "version" }
  | { state: "valid"; target: ConversationTarget };

export interface ConversationDestination {
  sessionId: string;
  target?: ConversationTarget;
}

export function isCanonicalConversationId(value: string): boolean {
  return UUID_RE.test(value);
}

function hasDuplicate(params: URLSearchParams, key: string): boolean {
  return params.getAll(key).length > 1;
}

export function parseConversationTarget(hash: string): ConversationTargetParseResult {
  if (!hash || hash === "#") return { state: "none" };
  const fragment = hash.startsWith("#") ? hash.slice(1) : hash;
  if (fragment.length > MAX_FRAGMENT_LENGTH) return { state: "invalid", reason: "oversized" };

  const params = new URLSearchParams(fragment);
  const supportedKeys = ["nav", "message", "heading", "thread", "turn"];
  if ([...params.keys()].some((key) => !supportedKeys.includes(key))) {
    return { state: "invalid", reason: "ambiguous" };
  }
  if (supportedKeys.some((key) => hasDuplicate(params, key))) {
    return { state: "invalid", reason: "duplicate" };
  }
  if (params.get("nav") !== "v1") return { state: "invalid", reason: "version" };

  const messageId = params.get("message") ?? undefined;
  const headingKey = params.get("heading") ?? undefined;
  const threadId = params.get("thread") ?? undefined;
  const turnId = params.get("turn") ?? undefined;
  if (
    (turnId && (messageId || headingKey || threadId))
    || (headingKey && (!messageId || threadId))
    || (!turnId && !messageId && !threadId)
  ) {
    return { state: "invalid", reason: "ambiguous" };
  }

  if (turnId) {
    return isCanonicalConversationId(turnId)
      ? { state: "valid", target: { kind: "turn", turnId } }
      : { state: "invalid", reason: "identifier" };
  }
  if (threadId) {
    if (!isCanonicalConversationId(threadId) || (messageId && !isCanonicalConversationId(messageId))) {
      return { state: "invalid", reason: "identifier" };
    }
    return {
      state: "valid",
      target: { kind: "thread", threadId, ...(messageId ? { messageId } : {}) },
    };
  }
  if (!messageId || !isCanonicalConversationId(messageId)) {
    return { state: "invalid", reason: "identifier" };
  }
  if (headingKey && !HEADING_KEY_RE.test(headingKey)) {
    return { state: "invalid", reason: "identifier" };
  }
  return {
    state: "valid",
    target: { kind: "message", messageId, ...(headingKey ? { headingKey } : {}) },
  };
}

export function conversationTargetFragment(target: ConversationTarget): string {
  const params = new URLSearchParams({ nav: "v1" });
  if (target.kind === "turn") params.set("turn", target.turnId);
  if (target.kind === "message") {
    params.set("message", target.messageId);
    if (target.headingKey) params.set("heading", target.headingKey);
  }
  if (target.kind === "thread") {
    params.set("thread", target.threadId);
    if (target.messageId) params.set("message", target.messageId);
  }
  return `#${params.toString()}`;
}

export function conversationDestinationHref(
  destination: ConversationDestination,
  options: { search?: string; canonical?: boolean } = {},
): string {
  const pathname = `/session/${encodeURIComponent(destination.sessionId)}`;
  const search = options.canonical ? "" : (options.search ?? "");
  const normalizedSearch = search && !search.startsWith("?") ? `?${search}` : search;
  return `${pathname}${normalizedSearch}${destination.target ? conversationTargetFragment(destination.target) : ""}`;
}

export function canonicalConversationPermalink(
  origin: string,
  destination: ConversationDestination,
): string | null {
  if (!isCanonicalConversationId(destination.sessionId)) return null;
  const ids = destination.target?.kind === "message"
    ? [destination.target.messageId]
    : destination.target?.kind === "turn"
      ? [destination.target.turnId]
      : destination.target?.kind === "thread"
        ? [destination.target.threadId, destination.target.messageId].filter(Boolean) as string[]
        : [];
  if (ids.some((id) => !isCanonicalConversationId(id))) return null;
  return new URL(conversationDestinationHref(destination, { canonical: true }), origin).toString();
}

export function pushConversationDestination(destination: ConversationDestination): void {
  window.history.pushState(null, "", conversationDestinationHref(destination, {
    search: destination.sessionId === sessionIdFromPath(window.location.pathname)
      ? window.location.search
      : "",
  }));
  window.dispatchEvent(new PopStateEvent("popstate"));
}

function sessionIdFromPath(pathname: string): string | null {
  const match = pathname.match(/^\/session\/([^/]+)$/);
  return match ? decodeURIComponent(match[1]) : null;
}
